import { useState } from 'react';
import { Button } from '@savvy-kit/components/button';
import { Skeleton } from '@savvy-kit/components/skeleton';
import { DrawIn, FadeIn } from '@savvy-kit/foundations/motion/chart-motion';
import { ALLOCATION_FILL, DELAY_STAT_STEP } from '@savvy-kit/foundations/motion/tokens';
import { Text } from '@savvy-kit/foundations/text';
import { isNil } from 'es-toolkit';

import type { ClientPortalAllocationSlice } from '../common/allocation';
import { foldAllocationTail, formatAllocationPercent } from '../common/allocation';
import { AllocationBar } from '../common/allocation-bar';
import { CLIENT_PORTAL_RETRY_DESCRIPTION } from '../common/constants';
import { PortalCardError } from '../common/portal-card-error';
import { PortalCardLinkAction } from '../common/portal-card-link-action';
import { PortalCard } from '../common/portal-section';
import {
  HOME_ALLOCATION_EMPTY_MESSAGE,
  HOME_ALLOCATION_ERROR_TITLE,
  HOME_ALLOCATION_LEGEND_CAP,
  HOME_ALLOCATION_SEE_ALL_LABEL,
  HOME_ALLOCATION_SHOW_LESS_LABEL,
  HOME_ALLOCATION_TITLE,
  HOME_ALLOCATION_VIEW_DETAILS_LABEL,
  HOME_BOTTOM_ROW_DELAY,
} from './constants';

type HomeAllocationCardProps = {
  readonly loading: boolean;
  readonly failed: boolean;
  readonly onRetry: () => void;
  readonly slices: readonly ClientPortalAllocationSlice[];
  readonly detailsHref: string | null;
  readonly delayOffset?: number;
};

type AllocationLegendProps = {
  readonly slices: readonly ClientPortalAllocationSlice[];
  readonly delay: number;
};

function legendRowDelay(index: number, pageArrivalDelay: number): number {
  const partOfPageArrival = index < HOME_ALLOCATION_LEGEND_CAP;
  return partOfPageArrival ? pageArrivalDelay + ALLOCATION_FILL.duration + index * DELAY_STAT_STEP : 0;
}

// The legend follows the bar once it has filled.
function AllocationLegend({ slices, delay }: AllocationLegendProps) {
  return (
    <ul className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
      {slices.map((slice, i) => (
        <FadeIn key={slice.name} delay={legendRowDelay(i, delay)}>
          <li className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-2">
              <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: slice.color }} />
              <Text variant="body-sm" color="dark" className="truncate">
                {slice.name}
              </Text>
            </span>
            <Text variant="body-sm" color="secondary" className="shrink-0 tabular-nums">
              {formatAllocationPercent(slice.percent)}
            </Text>
          </li>
        </FadeIn>
      ))}
    </ul>
  );
}

const SKELETON_LEGEND_ROWS = Array.from(
  { length: HOME_ALLOCATION_LEGEND_CAP },
  (_unused, index) => `allocation-placeholder-${index}`,
);

// The bar, a legend the size of the capped one, and the See all button's slot, so the card keeps its height.
function AllocationSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <Skeleton className="h-3 w-full rounded-full" />
      <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
        {SKELETON_LEGEND_ROWS.map((row) => (
          <div key={row} className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 flex-1 items-center gap-2">
              <Skeleton className="size-2 shrink-0 rounded-full" />
              <Skeleton className="h-5 w-28 max-w-full" />
            </span>
            <Skeleton className="h-5 w-9 shrink-0" />
          </div>
        ))}
      </div>
      <Skeleton className="h-8 w-20" />
    </div>
  );
}

export function HomeAllocationCard({
  loading,
  failed,
  onRetry,
  slices: allSlices,
  detailsHref,
  delayOffset = 0,
}: HomeAllocationCardProps) {
  const [expanded, setExpanded] = useState(false);
  const slices = foldAllocationTail(allSlices);
  const delay = delayOffset + HOME_BOTTOM_ROW_DELAY;
  const action = isNil(detailsHref) ? undefined : (
    <PortalCardLinkAction href={detailsHref} label={HOME_ALLOCATION_VIEW_DETAILS_LABEL} />
  );

  if (failed) {
    return (
      <PortalCard title={HOME_ALLOCATION_TITLE} padded={false}>
        <PortalCardError
          title={HOME_ALLOCATION_ERROR_TITLE}
          description={CLIENT_PORTAL_RETRY_DESCRIPTION}
          onRetry={onRetry}
        />
      </PortalCard>
    );
  }

  if (loading) {
    return (
      <PortalCard title={HOME_ALLOCATION_TITLE} padded={false}>
        <div className="px-6 pb-5">
          <AllocationSkeleton />
        </div>
      </PortalCard>
    );
  }

  if (slices.length === 0) {
    return (
      <PortalCard title={HOME_ALLOCATION_TITLE} padded={false}>
        <div className="px-6 pb-5">
          <Text variant="body-sm" color="secondary">
            {HOME_ALLOCATION_EMPTY_MESSAGE}
          </Text>
        </div>
      </PortalCard>
    );
  }

  return (
    <PortalCard title={HOME_ALLOCATION_TITLE} action={action} padded={false}>
      <div className="flex flex-col gap-4 px-6 pb-5">
        <DrawIn from={0.6} delay={delay} duration={ALLOCATION_FILL.duration} round="999px">
          <AllocationBar slices={slices} />
        </DrawIn>
        <AllocationLegend slices={expanded ? slices : slices.slice(0, HOME_ALLOCATION_LEGEND_CAP)} delay={delay} />
        {slices.length > HOME_ALLOCATION_LEGEND_CAP && (
          <Button
            variant="ghost"
            size="sm"
            className="self-start text-dark"
            onClick={() => {
              setExpanded(!expanded);
            }}
          >
            {expanded ? HOME_ALLOCATION_SHOW_LESS_LABEL : HOME_ALLOCATION_SEE_ALL_LABEL}
          </Button>
        )}
      </div>
    </PortalCard>
  );
}
